/**
 * Warms the cache entries a sidebar destination reads on first render, so a
 * hover fetches the page's data alongside its code (`route-loaders.ts`) rather
 * than after it. Each entry must build its queries from the same options
 * factories the page uses, or the warmed entry and the page's key diverge and
 * the page fetches again.
 *
 * Kept free of page components: this module is in the initial bundle.
 */
import type { QueryClient } from '@tanstack/react-query';
import type { PracticeType } from '@packages/types';
import { clientListQueryOptions, DEFAULT_CLIENT_LIST_FILTERS } from '../routes/clients.queries';
import { invoiceListQueryOptions, DEFAULT_INVOICE_LIST_FILTERS } from '../routes/invoices.queries';
import { clientSearchQueryOptions } from '../hooks/useClientSearch';
import { resolvedClientProjectsQueryOptions } from '../hooks/useResolvedClientProjects';
import {
  DEFAULT_INCOME_STATEMENT_PRESET,
  groupedIncomeStatementQueryOptions,
  ledgersQueryOptions,
  trialBalanceQueryOptions,
} from '../routes/ledger/ledger.queries';
import { selectBusinessLedger } from '../routes/ledger/use-bill-ledger';
import { visibleReportTabsForAccount } from '../routes/ledger/report-tabs';

export interface RouteDataContext {
  queryClient: QueryClient;
  accountId: string;
  /** What decides the accounting page's opening report. */
  account: { practiceType: PracticeType | null | undefined; administered: boolean; isLoading: boolean };
}

type RouteDataPrefetch = (context: RouteDataContext) => Promise<void>;

async function prefetchInvoices({ queryClient, accountId }: RouteDataContext) {
  void queryClient.prefetchInfiniteQuery(clientSearchQueryOptions(accountId, ''));
  // The list's matter names come from a second read keyed on the first page's
  // matter ids, so chaining it here is what saves the page its second wave.
  const invoices = await queryClient.fetchInfiniteQuery(invoiceListQueryOptions(accountId, DEFAULT_INVOICE_LIST_FILTERS));
  const matterIds = invoices.pages.flatMap((page) => page.data.map((invoice) => invoice.clientProjectId));
  if (matterIds.some(Boolean)) {
    await queryClient.prefetchQuery(resolvedClientProjectsQueryOptions(accountId, matterIds));
  }
}

/**
 * The accounting page resolves the business ledger, then reads the trial
 * balance to choose between setup and the tabs, then mounts the opening
 * report. Only the first step is a real dependency: the trial balance and the
 * report are fetched together here. For a ledger with no accounts the page
 * shows setup and the report read goes unused.
 */
async function prefetchAccountingLanding({ queryClient, accountId, account }: RouteDataContext) {
  const ledgers = await queryClient.fetchQuery(ledgersQueryOptions(accountId));
  const ledger = selectBusinessLedger(ledgers.data);
  if (!ledger) return;

  const reads = [queryClient.prefetchQuery(trialBalanceQueryOptions(accountId, ledger.id))];
  const [openingReport] = visibleReportTabsForAccount({
    ledgerKind: ledger.kind,
    practiceType: account.practiceType,
    isAccountLoading: account.isLoading,
    administered: account.administered,
  });
  if (openingReport.id === 'income-statement') {
    reads.push(queryClient.prefetchQuery(
      groupedIncomeStatementQueryOptions(accountId, ledger.id, { period: DEFAULT_INCOME_STATEMENT_PRESET }),
    ));
  }
  await Promise.all(reads);
}

/** Keyed by sidebar path. The sidebar only links to `/accounting` once access is granted. */
const sidebarRouteData: Record<string, RouteDataPrefetch> = {
  '/clients': async ({ queryClient, accountId }) => {
    await queryClient.prefetchInfiniteQuery(clientListQueryOptions(accountId, DEFAULT_CLIENT_LIST_FILTERS));
  },
  '/invoices': prefetchInvoices,
  '/accounting': prefetchAccountingLanding,
};

/**
 * Starts the data reads for a sidebar destination. A failed read is left for
 * the page's own query to retry and report, as `prefetchQuery` does.
 */
export function prefetchRouteData(path: string, context: RouteDataContext): void {
  const prefetch = sidebarRouteData[path];
  if (prefetch) prefetch(context).catch(() => undefined);
}
